import { Card, Grid, Icon, IconButton, InputLabel, Tooltip } from "@mui/material";
import MDInput from "components/MDInput";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDModal from "components/MDModal";
import { MDNoSelectedClient } from "components/MDNoSelectedClient";
import MDSelect from "components/MDSelect";
import MDTypography from "components/MDTypography";
import { useMaterialUIController } from "context";
import { useUser } from "context/UserContext";
import DataTable from "examples/Tables/DataTable";
import { Form, Formik } from "formik";
import DefaultCell from "layouts/dashboards/sales/components/DefaultCell";
import { useCallback, useEffect, useRef, useState } from "react";
import { toDateTime } from "utils/DateUtils";
import { errorAlert, formatarNumeroCNJ, infoAviso, successAlert } from "utils/Functions";
import { get, post, postNoResponse, delNoResponse } from "utils/WebService";
import CasesCreateEdit from "./CasesCreateEdit";

export default function Cases({ id = null, idDeadLine = null, initialTab = null, table = null, onBack = null, }) {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  const tableState = useRef({
    pageSize: 10,
    pageIndex: 0,
    sorting: [],
    filtered: [],
    clienteId: null,
    status: null,
    equipeId: null
  });

  const [data, setData] = useState([]);
  const [pages, setPages] = useState(0);
  const [loadingTable, setLoadingTable] = useState(true);
  const [isCreateEdit, setIsCreateEdit] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [recordsTotal, setRecordsTotal] = useState(0);
  const [aplicar, setAplicar] = useState(false);
  const [isLoadingInfoCliente, setIsLoadingInfoCliente] = useState(false);
  const [clienteList, setClienteList] = useState([]);
  const [equipeList, setEquipeList] = useState([]);
  const [isLoadingInfoEquipe, setIsLoadingInfoEquipe] = useState(false);
  const [appliedFilter, setAppliedFilter] = useState(false);
  const [showTable, setShowTable] = useState(false);
  const [isModal, setIsModal] = useState(false);
  const [dados, setDados] = useState(null);
  const [header, setHeader] = useState(null);

  const { statusCasoProcessual, sistemasEnum, userPerfils } = useUser();
  const isAdmin = userPerfils?.some(profile => profile.nome === "Administrador");

  const statusCasoProcessualWithAll = [{ id: null, name: "Todos", code: "todos" }, ...statusCasoProcessual];
  const sistemasEnumWithAll = [{ id: null, name: "Todos", code: "todos" }, ...sistemasEnum];

  const fetchData = useCallback(({ pageSize, pageIndex, sorting, clienteId, status, parteContraria, numeroProcesso, nomeCaso, ccDocumento, diasSemMovimentacao, processoEletronico, equipeId, isPolling = false }) => {
    if (!isPolling) setLoadingTable(true);

    const updatedTableState = {
      pageSize: pageSize ?? tableState.current.pageSize,
      pageIndex: pageIndex ?? tableState.current.pageIndex,
      sorting: sorting ?? tableState.current.sorting,
      clienteId,
      status: status?.id ?? tableState.current?.status ?? null,
      parteContraria,
      numeroProcesso,
      nomeCaso,
      ccDocumento,
      diasSemMovimentacao,
      processoEletronico,
      equipeId
    };

    tableState.current = updatedTableState;

    post("/processo/list", {
      page: updatedTableState.pageIndex,
      pageSize: updatedTableState.pageSize,
      sorted: updatedTableState.sorting,
      filtered: [],
      clienteId: updatedTableState.clienteId,
      status: updatedTableState.status,
      parteContraria: updatedTableState.parteContraria,
      numeroProcesso: updatedTableState.numeroProcesso,
      nomeCaso: updatedTableState.nomeCaso,
      ccDocumento: updatedTableState.ccDocumento,
      diasSemMovimentacao: updatedTableState.diasSemMovimentacao,
      processoEletronico: Array.isArray(updatedTableState.processoEletronico)
        ? (updatedTableState.processoEletronico.some(x => x?.code === "todos")
            ? null
            : (updatedTableState.processoEletronico.length > 0 ? updatedTableState.processoEletronico.map(x => x?.id).filter(id => id != null) : null))
        : (updatedTableState.processoEletronico && updatedTableState.processoEletronico.code !== "todos" 
            ? [updatedTableState.processoEletronico.id] 
            : null),
      equipeId: updatedTableState.equipeId

    }).then(({ data, pages, recordsTotal }) => {
      setPages(pages);
      setData(data);
      setRecordsTotal(recordsTotal);
      if (!isPolling) setLoadingTable(false);
      setAplicar(false);
    }).catch(() => {
      if (!isPolling) setLoadingTable(false);
      setAplicar(false);
    });

  }, []);

  const backFunction = () => {
    if (onBack) {
      onBack();
      return;
    }

    fetchData({ ...tableState.current });
    setIsCreateEdit(false);
    setSelectedId(null);
  };


  useEffect(() => {
    if (id) {
      setSelectedId(id);
      setIsCreateEdit(true);
    }
  }, [id]);

  useEffect(() => {
    setIsLoadingInfoCliente(true);
    get("/pessoa/list-all")
      .then(({ list }) => setClienteList(list))
      .catch(() => setClienteList([]))
      .finally(() => setIsLoadingInfoCliente(false));
      
    setIsLoadingInfoEquipe(true);
    get("/equipe/list-all")
      .then(({ list }) => setEquipeList(list))
      .catch(() => setEquipeList([]))
      .finally(() => setIsLoadingInfoEquipe(false));
  }, []);

  useEffect(() => {
    let intervalId;
    if (appliedFilter && showTable && !isCreateEdit) {
      intervalId = setInterval(() => {
        fetchData({ ...tableState.current, isPolling: true });
      }, 30000); // 30 segundos
    }
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [appliedFilter, showTable, isCreateEdit, fetchData]);

  const handleReprocess = (id) => {
    infoAviso(
      "Deseja realmente atualizar o processo?",
      async () => { await reprocess(id); },
      () => { return; }
    );
  };

  const reprocess = async (id) => {
    if (!id) return;
    postNoResponse(`/processo-reprocess/by-id/${id}`)
      .then(() => { successAlert("Atualização iniciada com sucesso!", () => null); fetchData({ ...tableState.current }); })
      .catch(() => errorAlert("Erro ao iniciar a atualização!"));
  };

  const handleDelete = (id) => {
    infoAviso(
      "Deseja realmente excluir este processo e todos os seus vínculos? Essa ação não poderá ser desfeita.",
      async () => { await deleteProcess(id); },
      () => { return; }
    );
  };

  const deleteProcess = async (id) => {
    if (!id) return;
    delNoResponse(`/processo/delete/${id}`)
      .then(() => { 
        successAlert("Processo excluído com sucesso!", () => null); 
        fetchData({ ...tableState.current }); 
      })
      .catch(() => errorAlert("Erro ao excluir o processo!"));
  };

  const handleOpenModal = (dados, header) => {
    setDados(dados);
    setHeader(header);
    setIsModal(true);
  };

  const handleCloseModal = () => {
    setIsModal(false);
    setDados(null);
    setHeader(null);
  };

  const userColumns = [
    {
      header: "processo",
      accessorKey: "processo",
      cell: info => <DefaultCell>{formatarNumeroCNJ(info.getValue() ?? "")}</DefaultCell>,
      isSorted: true
    },
    {
      header: "parte contrária",
      accessorKey: "parteContraria",
      cell: info => <DefaultCell>{info.getValue() || ""}</DefaultCell>,
      isSorted: true
    },
    {
      header: "cliente",
      accessorKey: "cliente.nomeComum",
      cell: info => <DefaultCell>{info.getValue() || ""}</DefaultCell>,
      isSorted: true
    },
    {
      header: "status",
      accessorKey: "status",
      cell: info => {
        const status = statusCasoProcessual?.find(ht => ht.code === info.getValue());
        return <DefaultCell>{status ? status.name : "-"}</DefaultCell>;
      },
      isSorted: true
    },
    {
      header: "Processo eletrônico",
      accessorKey: "processoEletronico",
      cell: info => {
        const processo = sistemasEnum?.find(ht => ht.code === info.getValue());
        return <DefaultCell>{processo ? processo.name : "-"}</DefaultCell>;
      },
      isSorted: true,
      width: "9%"
    },
    {
      header: "Dias s/ movimentações",
      accessorKey: "diasSemMovimentacao",
      cell: info => <DefaultCell>{`${info.getValue() || ""}`}</DefaultCell>,
      isSorted: true,
      width: "10%"
    },
    {
      header: "última atualização",
      accessorKey: "ultimaAtualizacao",
      cell: info => <DefaultCell>{toDateTime(info.getValue()) || '-'}</DefaultCell>,
      isSorted: true
    },
    {
      header: "próxima atualização",
      accessorKey: "proximaExecucao",
      cell: info => <DefaultCell>{toDateTime(info.getValue()) || '-'}</DefaultCell>,
      isSorted: true
    },
    {
      header: "Mensagem",
      accessorKey: "mensagem",
      cell: info => (
        info.getValue()?.length > 0 && (
          <IconButton onClick={() => handleOpenModal(info.getValue(), 'Mensagem')}>
            <MDTypography variant="h4">
              <Icon color="inherit" sx={{ mr: 1 }}>description</Icon>
            </MDTypography>
          </IconButton>
        )
      ),
      isSorted: true,
      width: "7%"
    },
    {
      header: "ações",
      accessorKey: "status",
      isSorted: false,
      cell: info => (
        <>
          {["concluido", "erro"].includes(info.getValue()) &&
            <Tooltip title="Editar">
              <IconButton onClick={() => { setSelectedId(info.row.original?.id); setIsCreateEdit(true); }}>
                <MDTypography variant="h4">
                  <Icon color="inherit" sx={{ mr: 1 }}>edit</Icon>
                </MDTypography>
              </IconButton>
            </Tooltip>
          }
          {["concluido", "erro"].includes(info.getValue()) &&
            <Tooltip title="Atualizar">
              <IconButton onClick={() => handleReprocess(info.row.original?.id)}>
                <MDTypography variant="h4">
                  <Icon color="inherit" sx={{ mr: 1 }}>refresh</Icon>
                </MDTypography>
              </IconButton>
            </Tooltip>
          }
          {isAdmin &&
            <Tooltip title="Excluir">
              <IconButton onClick={() => handleDelete(info.row.original?.id)}>
                <MDTypography variant="h4">
                  <Icon color="error" sx={{ mr: 1 }}>delete</Icon>
                </MDTypography>
              </IconButton>
            </Tooltip>
          }
        </>
      ),
      width: "8%"
    },
  ];

  const handleCleanFilter = (resetForm) => {
    resetForm();
    setAppliedFilter(false);
    setShowTable(false);
    setData([]);
    tableState.current = {
      pageSize: 10,
      pageIndex: 0,
      sorting: [],
      filtered: [],
      clienteId: null,
      status: null,
      equipeId: null,
      ccDocumento: null,
    };
  };

  return (
    <Grid container display="flex" justifyContent="center">
      {isModal &&
        <MDModal header={header} dados={dados} show={isModal} close={handleCloseModal} />
      }

      {isCreateEdit &&
        <Grid item xs={12} md={12}>
          <CasesCreateEdit
            id={selectedId}
            closeCreateEdit={backFunction}
            idDeadLine={idDeadLine}
            initialTab={initialTab}
            table={table}
          />
        </Grid>
      }

      <Grid item xs={12} md={12} sx={{ display: isCreateEdit ? "none" : "" }}>
        <Card
          variant="outlined"
          sx={{
            px: 2,
            py: 1.5,
            mb: 2,
            background: darkMode ? "#292C37" : "#fafafa",
            borderColor: darkMode ? "#333" : "#ddd",
          }}
        >
          <Grid container spacing={1}>
            <Formik
              initialValues={{
                clienteId: null,
                status: null,
                equipeId: null,
                parteContraria: '',
                numeroProcesso: '',
                nomeCaso: '',
                ccDocumento: '',
                diasSemMovimentacao: null,
                processoEletronico: []
              }}
              onSubmit={(values) => {
                fetchData({
                  pageSize: tableState.current.pageSize,
                  pageIndex: 0,
                  clienteId: values.clienteId?.id ?? values.clienteId ?? null,
                  status: values.status?.id ?? values.status ?? null,
                  equipeId: values.equipeId?.id ?? values.equipeId ?? null,
                  processoEletronico: values.processoEletronico,
                  numeroProcesso: values.numeroProcesso || null,
                  nomeCaso: values.nomeCaso || null,
                  ccDocumento: values.ccDocumento || null,
                  parteContraria: values.parteContraria || null,
                  diasSemMovimentacao: values.diasSemMovimentacao ?? null,
                });

                setAppliedFilter(true);
                setShowTable(true);
              }}
            >
              {({ values, setFieldValue, resetForm }) => (
                <Form>
                  <Grid container spacing={2}>
                    <Grid container spacing={2} mt={1} ml={0} alignItems="flex-start" >
                      <Grid item xs={12} sm={3}>
                        <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Cliente</InputLabel>
                        <MDSelect
                          name='clienteId'
                          isLoading={isLoadingInfoCliente}
                          getOptionValue={(option) => option.id}
                          getOptionLabel={(option) => option.nomeComum}
                          placeholder="Selecione o cliente"
                          value={values.clienteId || null}
                          options={clienteList}
                          onChange={(val) => setFieldValue('clienteId', val || null)}
                        />
                      </Grid>
                      <Grid item xs={12} sm={9} display="flex" justifyContent="flex-end" alignItems="center">
                        <MDButton
                          size="medium"
                          variant="contained"
                          color="info"
                          onClick={() => {
                            setSelectedId(null);
                            setIsCreateEdit(true);
                          }}
                        >
                          Novo Processo
                        </MDButton>
                      </Grid>
                    </Grid>

                    <Grid item xs={12} sm={3}>
                      <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Parte Contrária</InputLabel>
                      <MDInput
                        size="small"
                        fullWidth
                        placeholder="Informe a parte contrária..."
                        value={values.parteContraria}
                        onChange={(e) => setFieldValue('parteContraria', e.target.value)}
                      />
                    </Grid>

                    <Grid item xs={12} sm={3}>
                      <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>N° Processo</InputLabel>
                      <MDInput
                        size="small"
                        fullWidth
                        placeholder="Informe o nº do processo"
                        value={values.numeroProcesso}
                        onChange={(e) => setFieldValue('numeroProcesso', e.target.value)}
                      />
                    </Grid>

                    <Grid item xs={12} sm={3}>
                      <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Nome do Caso</InputLabel>
                      <MDInput
                        size="small"
                        fullWidth
                        placeholder="Informe o nome do caso"
                        value={values.nomeCaso}
                        onChange={(e) => setFieldValue('nomeCaso', e.target.value)}
                      />
                    </Grid>

                    <Grid item xs={12} sm={3}>
                      <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Status</InputLabel>
                      <MDSelect
                        name="status"
                        getOptionValue={(option) => option.code}
                        getOptionLabel={(option) => option.name}
                        value={values.status || null}
                        placeholder="Selecione"
                        options={statusCasoProcessualWithAll}
                        onChange={(val) => setFieldValue('status', val || null)}
                      />
                    </Grid>

                    <Grid item xs={12} sm={3}>
                      <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Dias s/ Movimentações</InputLabel>
                      <MDInput
                        size="small"
                        fullWidth
                        type="number"
                        value={values.diasSemMovimentacao ?? ''}
                        placeholder="Informe a quantidade de dias"
                        onChange={(e) =>
                          setFieldValue(
                            'diasSemMovimentacao',
                            e.target.value === '' ? null : parseInt(e.target.value)
                          )
                        }
                      />
                    </Grid>

                    <Grid item xs={12} sm={3}>
                      <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Processo Eletrônico</InputLabel>
                      <MDSelect
                        isMulti
                        name='processoEletronico'
                        getOptionValue={option => option.code}
                        getOptionLabel={option => option.name}
                        placeholder="Selecione"
                        value={values.processoEletronico || []}
                        options={sistemasEnumWithAll}
                        onChange={(val) => {
                          if (val && val.some(x => x.code === "todos")) {
                            setFieldValue('processoEletronico', [sistemasEnumWithAll[0]]);
                          } else {
                            setFieldValue('processoEletronico', val || []);
                          }
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={3}>
                      <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Equipe</InputLabel>
                      <MDSelect
                        name='equipeId'
                        isLoading={isLoadingInfoEquipe}
                        getOptionValue={(option) => option.id}
                        getOptionLabel={(option) => option.nome}
                        placeholder="Selecione a equipe"
                        value={values.equipeId || null}
                        options={equipeList}
                        onChange={(val) => setFieldValue('equipeId', val || null)}
                      />
                    </Grid>

                    <Grid item xs={12} sm={3}>
                      <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Documento</InputLabel>
                      <MDInput
                        size="small"
                        fullWidth
                        placeholder="Informe o documento"
                        value={values.ccDocumento}
                        onChange={(e) => setFieldValue('ccDocumento', e.target.value)}
                      />
                    </Grid>

                    <Grid item xs={12} display="flex" justifyContent="flex-end">
                      <MDButton
                        variant="contained"
                        color="info"
                        sx={{ mr: 2 }}
                        type="button"
                        onClick={() => handleCleanFilter(resetForm)}
                      >
                        Limpar filtro
                      </MDButton>
                      <MDButton
                        variant="contained"
                        color="info"
                        type="submit"
                      >
                        Aplicar filtro
                      </MDButton>
                    </Grid>
                  </Grid>
                </Form>
              )}
            </Formik>
          </Grid>
        </Card>

        {(appliedFilter && showTable) ?
          <DataTable
            loading={loadingTable}
            columnData={userColumns}
            data={data}
            fetchData={fetchData}
            totalPages={pages}
            totalRecords={recordsTotal}
            tableState={tableState}
            changeFilter={aplicar}
          />
          :
          <MDNoSelectedClient wasWanted={false} isCase />
        }

      </Grid>
    </Grid>
  );
}
